/**
 * Story 3.11 — ClickFunnels confirmation-page order handback (AC1).
 *
 * The CF order-confirmation page's "Build your listing" button carries the
 * order id as `?cf_order=`. This module captures that value on arrival and
 * hands it to the existing account-setup convergence notify, so tph-api can
 * bind the paid slot to the account the buyer actually creates — even when
 * they type a different email than the one on their credit card.
 *
 * The value is CLIENT INPUT. The shape check here is hygiene, not
 * authorization: the server re-applies it, and every decision that matters is
 * made against the ClickFunnels API response in tph-api.
 *
 * Storage is sessionStorage, on the exact `hasWindow()`-guarded try/catch shape
 * used by `src/util/searchTelemetry.js` — blocked storage (private mode,
 * disabled cookies) must be a silent no-op, never a throw. SSR renders this
 * code, so nothing may touch `window` unguarded.
 *
 * The auth gate already carries the whole query string
 * (`Routes.js` → `state.from = pathname + search + hash`), so this is a guard
 * for a mid-signup refresh and a direct `/signup?cf_order=` arrival — not a new
 * carrier.
 */

const CF_ORDER_KEY = 'tph_cf_order';
const CF_ORDER_MAX_LENGTH = 128;
const CF_ORDER_PATTERN = /^[A-Za-z0-9_-]+$/;

const hasWindow = () => typeof window !== 'undefined';

/**
 * Normalize a candidate order id. Non-empty after trim, at most 128 chars, and
 * `[A-Za-z0-9_-]` only. Anything else is discarded silently, as if absent.
 *
 * @param {*} raw candidate value
 * @returns {string|null} the trimmed id, or null when malformed/absent
 */
export const normalizeCfOrder = raw => {
  if (typeof raw !== 'string') {
    return null;
  }
  const trimmed = raw.trim();
  if (!trimmed || trimmed.length > CF_ORDER_MAX_LENGTH || !CF_ORDER_PATTERN.test(trimmed)) {
    return null;
  }
  return trimmed;
};

/**
 * Read the stored order id for this session.
 *
 * @returns {string|null}
 */
export const readCfOrder = () => {
  if (!hasWindow()) {
    return null;
  }
  try {
    return normalizeCfOrder(window.sessionStorage.getItem(CF_ORDER_KEY));
  } catch (e) {
    return null;
  }
};

/**
 * Forget the stored order id. A cleared value is gone for the rest of the
 * session — a stale id must never ride a different signup.
 *
 * Review S-9 — LOGOUT IS ALSO COVERED, and not by this function. `reducers.js`
 * line 23 calls `window.sessionStorage.clear()` on `logoutThunk.fulfilled` — a
 * BLANKET clear, not a key-selective one, so `tph_cf_order` goes with
 * everything else and no call has to be added there. That closes the
 * shared-tab shape where an authenticated buyer arrives via
 * `/l/new?cf_order=`, never signs up, logs out, and a second person then
 * creates an account in the same tab. Residual, and NOT closed by this: a
 * visitor who was never authenticated has no logout to trigger it, so the
 * value survives until the tab closes — the general no-TTL property tracked as
 * review N-10.
 */
export const clearCfOrder = () => {
  if (!hasWindow()) {
    return;
  }
  try {
    window.sessionStorage.removeItem(CF_ORDER_KEY);
  } catch (e) {
    /* storage blocked — capture is best-effort */
  }
};

/**
 * Parse `cf_order` out of a query string and store it when well-formed. The
 * FIRST well-formed value wins: a later capture never overwrites a stored
 * value within the same session.
 *
 * @param {string} search a query string, with or without the leading `?`
 * @returns {string|null} the value now in storage, or null when there is none
 */
export const captureCfOrder = search => {
  if (!hasWindow() || typeof search !== 'string' || !search) {
    return readCfOrder();
  }
  const existing = readCfOrder();
  if (existing) {
    return existing;
  }
  let candidate = null;
  try {
    candidate = normalizeCfOrder(new URLSearchParams(search).get('cf_order'));
  } catch (e) {
    return null;
  }
  if (!candidate) {
    return null;
  }
  try {
    window.sessionStorage.setItem(CF_ORDER_KEY, candidate);
  } catch (e) {
    /* storage blocked — the value still rides this navigation's own param */
  }
  return candidate;
};

/* ────────────────────────────────────────────────────────────────────────────
 * Story 3.19 — the AUTHENTICATED-ARRIVAL consumer's two helpers.
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * Story 3.19 (ID-2) — read `cf_order` out of THIS NAVIGATION'S query string,
 * and nothing else.
 *
 * 🔴 WHY THIS EXISTS RATHER THAN REUSING `captureCfOrder`. `captureCfOrder` is
 * first-write-wins: when a value is already stored it returns the OLDER id and
 * never the one on the current URL. That is right for the signup flow it was
 * written for and PROVABLY WRONG here. TK bought twice in one session. On the
 * second arrival (`GkxvrP`, grant 149) `captureCfOrder(window.location.search)`
 * returns `QoLxnq` — the first order, by then already attached. The exact bind
 * would see a non-`held` row, `orderSpent` would fire, the order leg would
 * terminate, and grant 149 would never attach. The headline case would
 * half-fail. Pinned by AC2-T2.
 *
 * It reads ONLY the string it is handed and NEVER touches `sessionStorage`.
 * No param in this URL ⇒ null ⇒ the consumer makes no call at all (AC4 row 1).
 *
 * @param {string} search a query string, with or without the leading `?`
 * @returns {string|null} this navigation's order id, or null
 */
export const readCfOrderFromSearch = search => {
  if (typeof search !== 'string' || !search) {
    return null;
  }
  try {
    return normalizeCfOrder(new URLSearchParams(search).get('cf_order'));
  } catch (e) {
    return null;
  }
};

/**
 * Story 3.19 (ID-5) — the attempted-order suppressor.
 *
 * 🔴 IT IS A SET KEYED BY ORDER ID, NEVER A SINGLE SLOT, AND THIS IS THE ONE
 * THING ABOUT IT THAT MATTERS. The question it answers is "has THIS id been
 * attempted in this session?" — never "has ANY id been attempted?".
 *
 * The trap is easy to fall into because the wrong idiom is sitting a few lines
 * up: `captureCfOrder` is single-valued and first-write-wins. Mirroring it here
 * produces:
 *
 *   1. TK arrives with `QoLxnq` → notify fires → the slot is now occupied.
 *   2. TK buys again in the SAME session and arrives with `GkxvrP` → the slot
 *      is occupied → the call is swallowed → GRANT 149 NEVER ATTACHES.
 *
 * ID-2 fixes the read and that would re-break the write, from a different
 * line, with every other test still green. Pinned by AC3-T4.
 *
 * Bounded at 10 ids, oldest evicted first. Ten is far above any honest session
 * (TK, the heaviest real buyer on record, made two purchases) and far below
 * any storage concern. A FULL SET EVICTS, NEVER REFUSES: refusing to record is
 * harmless, but refusing to FIRE on an 11th distinct id would reintroduce
 * exactly the defect this exists to close. Eviction is not a correctness risk
 * — a re-presented evicted id simply fires again and the server no-ops it
 * (`orderSpent`, `ON CONFLICT (cf_order_id) DO NOTHING`).
 *
 * 🔒 THE SUPPRESSOR IS POLITENESS; THE SERVER OWNS IDEMPOTENCY. That asymmetry
 * is why "evict and re-fire" is always safe and "suppress and swallow" never
 * is — and it is why a storage failure degrades to NO suppression, never to
 * suppression.
 *
 * Stored under its own key so it cannot collide with `tph_cf_order`
 * (CF_ORDER_KEY) and so a reviewer can grep for it. `clearCfOrder()` is NOT
 * called on this path: clearing it would break the signup readers' contract
 * for a session that later signs out and signs up (AC8).
 */
const CF_ORDER_ATTEMPTED_KEY = 'tph_cf_order_attempted';
const CF_ORDER_ATTEMPTED_MAX = 10;

/** The stored set, oldest first. Any unreadable/!array value reads as empty. */
const readAttemptedCfOrders = () => {
  if (!hasWindow()) {
    return [];
  }
  try {
    const parsed = JSON.parse(window.sessionStorage.getItem(CF_ORDER_ATTEMPTED_KEY));
    return Array.isArray(parsed) ? parsed.filter(id => typeof id === 'string') : [];
  } catch (e) {
    // Blocked storage, or a corrupt/hand-edited value. Either way: no
    // suppression, so the call fires.
    return [];
  }
};

/**
 * Story 3.19 (ID-5) — has THIS id already been attempted in this session?
 *
 * @param {string|null} orderId
 * @returns {boolean} false whenever the answer is not a confident yes
 */
export const hasAttemptedCfOrder = orderId => {
  if (!orderId) {
    return false;
  }
  return readAttemptedCfOrders().includes(orderId);
};

/**
 * Story 3.19 (ID-5) — remember that `orderId` was attempted, evicting the
 * oldest id once the set is full.
 *
 * @param {string|null} orderId
 */
export const rememberAttemptedCfOrder = orderId => {
  if (!hasWindow() || !orderId) {
    return;
  }
  const existing = readAttemptedCfOrders();
  if (existing.includes(orderId)) {
    return;
  }
  // Oldest-first eviction. `slice(-MAX)` keeps the newest MAX and drops the
  // head, so the set can never grow without limit.
  const next = [...existing, orderId].slice(-CF_ORDER_ATTEMPTED_MAX);
  try {
    window.sessionStorage.setItem(CF_ORDER_ATTEMPTED_KEY, JSON.stringify(next));
  } catch (e) {
    /* storage blocked — suppression is best-effort, the attempt still fires */
  }
};

/* ────────────────────────────────────────────────────────────────────────────
 * Story 3.24 — the post-purchase DESTINATION: where the buyer was going.
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * The destination a signed-out ClickFunnels buyer was trying to reach —
 * `/l/new?cf_order=…`, exactly as `Routes.js` built it — remembered so the
 * auth flow can find its way back without the router.
 *
 * 🔴 WHY THIS EXISTS. Until 3.24 the destination rode ONLY in router
 * `state.from` (`NewListingCfOrderPrompt.js`), and nothing else held it. It
 * was lost by every password-reset path (`PasswordResetPage.js` sent everyone
 * to the landing page), by `MigrationRecognitionModal`'s signup link (a
 * NamedLink with no state), by a Google/Facebook error (`loginWithIdp.js`)
 * and by any full page load. Of 57 purchases in the 14-day window ending
 * 2026-09-25, 18 buyers arrived signed out and 11 of those went through a
 * password reset — the majority of the signed-out cohort landed on the
 * homepage with their order gone.
 *
 * 🔴 WHY THIS IS localStorage WHEN THE ORDER ID ABOVE IS sessionStorage.
 * sessionStorage is PER TAB. The largest real cohort — those 11 reset
 * buyers — reach the reset page by clicking the link in their email, which
 * opens a NEW tab with empty sessionStorage; a per-tab store cannot help
 * them at all (3.24 pass A). localStorage is shared across the origin's
 * tabs and survives them. Two things keep that from being a liability:
 *   - a stored TIMESTAMP and a 60-MINUTE EXPIRY that RESTARTS on every
 *     capture — a read past it returns null and clears the key, so a
 *     destination cannot outlive the sitting it belongs to (the order id
 *     keeps its no-TTL, per-tab shape; it is bound to an account by the
 *     server and is NOT moved). SIXTY, by operator ruling on the second 3.24
 *     cold review (pass C), after pass B had cut it to fifteen. Fifteen was
 *     sized on the FIRST reader — the reset buyers opened the reset link a
 *     median 1.3 minutes after arriving, never later than 9.5 — but the LAST
 *     reader is the onboarding success button (`OnboardingModal.js`), which
 *     comes after the reset, the sign-in, phone verification and terms. A
 *     window sized on the first reader lapses before the last one needs it,
 *     and a lapse sends a paid buyer to the page whose only door is the
 *     checkout. The clock restarts because a buyer who comes back to the
 *     claim page is still in the same sitting — the stamp measures the LAST
 *     arrival;
 *   - an EXPLICIT clear on logout in `reducers.js` — the blanket
 *     `sessionStorage.clear()` there does not reach localStorage, so without
 *     it a second person in the same browser would inherit the first one's
 *     order. `clearCfOrder` needs no such call; the blanket clear covers it.
 *
 * Same guards as the order id, for the same reasons: `hasWindow()`-guarded,
 * try/catch around every storage call — but LAST WRITE WINS (see
 * `captureCfDestination`), where the order id is first-write-wins. The value is CLIENT
 * INPUT that a page will `<Redirect>` to, so the shape check is not optional:
 * a SAME-ORIGIN PATH ONLY. Leading `/`, never `//` or `/\` (a browser reads
 * both as protocol-relative, i.e. another host), no whitespace or control
 * characters, bounded length. Anything else reads as absent, exactly like a
 * malformed order id — and so does a record whose timestamp is not a number.
 *
 * Stored shape: `{"path": <destination>, "storedAt": <epoch ms>}` under the
 * one key. A hand-edited or pre-JSON value is unreadable and reads as absent.
 */
const CF_DESTINATION_KEY = 'tph_cf_destination';
const CF_DESTINATION_MAX_LENGTH = 2048;
const CF_DESTINATION_PATTERN = /^\/(?![/\\])[^\s\x00-\x1f]*$/;
const CF_DESTINATION_TTL_MS = 60 * 60 * 1000;

/**
 * Normalize a candidate destination.
 *
 * @param {*} raw candidate value
 * @returns {string|null} the same-origin path, or null when malformed/absent
 */
export const normalizeCfDestination = raw => {
  if (typeof raw !== 'string') {
    return null;
  }
  if (!raw || raw.length > CF_DESTINATION_MAX_LENGTH || !CF_DESTINATION_PATTERN.test(raw)) {
    return null;
  }
  return raw;
};

/**
 * 🔴 Pass C (operator ruling on the second cold review, finding 5) — HOW LONG
 * AFTER IT EXPIRES A RECORD STILL COUNTS AS "THIS CLAIM'S". An expired record
 * is cleared by the read that finds it, never left for a later one; what the
 * page remembers of it (`expiredHere`, below) is reportable only while the
 * record is at most one more window past its expiry — so at most 120 minutes
 * after the buyer's last arrival at the claim page. Older than that it belongs
 * to a sitting that is over: it is cleared SILENTLY and reads as absent, so it
 * can never surface as `claim_ended_elsewhere` at an unrelated later sign-in.
 * One window, not a second tuned figure: the only claim a sign-in can still be
 * finishing is one whose own window closed less than a window ago.
 */
const CF_DESTINATION_REPORT_HORIZON_MS = CF_DESTINATION_TTL_MS;

/**
 * Pass C — the record THIS PAGE found expired and cleared, kept in memory (per
 * page session, never in storage) so the sign-in that finishes on this page can
 * still report that the claim ran out (`cfClaimSignals.js`). Without it the
 * clear-on-read would erase the only evidence before the report could see it,
 * and "expired during this claim" would read the same as "nothing stored".
 * Forgotten by `clearCfDestination` (a report, the onboarding button, logout)
 * and by a new capture.
 */
let expiredHere = null;

/**
 * 🔴 Pass C (operator ruling on the second cold review, finding 1) — THE RECORD
 * A `/login` REDIRECT USED, HANDED FROM STORAGE TO THIS PAGE'S MEMORY.
 *
 * `AuthenticationPage` used to DELETE the record once it had driven the
 * redirect. That kept the shared-browser bound (#1279) — nobody signing in
 * later can be routed by it — but the same page's onboarding success button
 * (`OnboardingModal.js`), the LAST reader, then found nothing: it fell through
 * to the audience route (`/listings` for a `migrated`-audience seller), and
 * the claim-flow signal, seeing `absent`, stayed silent on a real misroute.
 *
 * `handOffCfDestination` keeps both. The record LEAVES localStorage exactly as
 * before — every other tab, every later page load and every later sign-in in
 * the browser finds nothing — and stays in THIS page's memory, where only the
 * reader that asks for it (`inspectCfDestination({ includeHandedOff: true })`:
 * the onboarding button) can see it. Same 60-minute window; once past it, that
 * read moves it into `expiredHere`, which is what the claim-flow signal reports
 * from. Forgotten by `clearCfDestination` — so
 * logout, the button's own use and a report all drop it — by a new capture,
 * and by the page going away.
 */
let handedOff = null;

const readStoredDestination = () => {
  let record = null;
  try {
    record = JSON.parse(window.localStorage.getItem(CF_DESTINATION_KEY));
  } catch (e) {
    // Blocked storage, or a corrupt/hand-edited/pre-JSON value: absent.
    return null;
  }
  const path = normalizeCfDestination(record?.path);
  const storedAt = record?.storedAt;
  if (!path || typeof storedAt !== 'number' || !Number.isFinite(storedAt)) {
    return null;
  }
  return { path, storedAt };
};

const removeStoredDestination = () => {
  try {
    window.localStorage.removeItem(CF_DESTINATION_KEY);
  } catch (e) {
    /* storage blocked — capture is best-effort */
  }
};

const withinReportHorizon = storedAt =>
  Date.now() - storedAt <= CF_DESTINATION_TTL_MS + CF_DESTINATION_REPORT_HORIZON_MS;

/**
 * Forget the stored destination — and what this page remembered of an expired
 * one. Called by the last consumer on each path (`AuthenticationPage`'s
 * stored-value redirect, the onboarding success CTA), by the claim-flow signal
 * once it has reported, and EXPLICITLY on logout by `reducers.js` — the
 * blanket `sessionStorage.clear()` there does not reach this store.
 */
export const clearCfDestination = () => {
  if (!hasWindow()) {
    return;
  }
  expiredHere = null;
  handedOff = null;
  removeStoredDestination();
};

/**
 * Pass C — move the stored record from localStorage into this page's memory
 * (see `handedOff`). Called by `AuthenticationPage` once the stored value has
 * driven its redirect, where it used to call `clearCfDestination`. An expired
 * or malformed record is not handed off; it simply leaves storage.
 */
export const handOffCfDestination = () => {
  if (!hasWindow()) {
    return;
  }
  const record = readStoredDestination();
  if (record && Date.now() - record.storedAt <= CF_DESTINATION_TTL_MS) {
    handedOff = record;
  }
  removeStoredDestination();
};

const ABSENT_DESTINATION = { status: 'absent', path: null, storedAt: null };

/**
 * Look at the destination: `{ status: 'live' | 'expired' | 'absent', path,
 * storedAt }`. Malformed, blocked or absent all read as `absent`.
 *
 * 🔴 Pass C (ruling on finding 5) — AN EXPIRED RECORD IS CLEARED BY THE READ
 * THAT FINDS IT. Pass B made this a pure look so the claim-flow signal could
 * still see a record that had run out; the price was that an abandoned record
 * sat in localStorage with no end date and surfaced as `expired` at whatever
 * sign-in came next in that browser, however much later. Now the first read
 * past the window removes it from storage, and THIS PAGE remembers it
 * (`expiredHere`) only while it is within the report horizon — so a sign-in
 * finishing on this page still reads `expired`, while a record from a sitting
 * that is over reads `absent`. The boot sweep (`src/index.js`) makes the next
 * page load in the browser the latest a record can sit past its window.
 *
 * Idempotent, so it is safe in a render that React may invoke twice: the second
 * pass finds storage empty and reads the same memory.
 *
 * @param {Object} [options]
 * @param {boolean} [options.includeHandedOff] also consult the record this page's
 *   `/login` redirect handed off (`handOffCfDestination`) — the onboarding button
 *   only; never a sign-in fallback
 * @returns {{status: string, path: (string|null), storedAt: (number|null)}}
 */
export const inspectCfDestination = ({ includeHandedOff = false } = {}) => {
  if (!hasWindow()) {
    return ABSENT_DESTINATION;
  }
  const stored = readStoredDestination();
  // A record in storage is always the newer claim; the handed-off one is read
  // only by the callers that ask for it, and only when storage is empty.
  const record = stored || (includeHandedOff ? handedOff : null);
  if (record) {
    if (Date.now() - record.storedAt <= CF_DESTINATION_TTL_MS) {
      return { status: 'live', ...record };
    }
    if (record === stored) {
      removeStoredDestination();
    } else {
      handedOff = null;
    }
    expiredHere = record;
  }
  if (expiredHere && !withinReportHorizon(expiredHere.storedAt)) {
    expiredHere = null;
  }
  return expiredHere ? { status: 'expired', ...expiredHere } : ABSENT_DESTINATION;
};

/**
 * Read the stored destination: the path when a well-formed record exists and
 * is within its 60-minute window, else null. An EXPIRED record is cleared on
 * the way out (by `inspectCfDestination`), so the next capture is free to
 * write. The boundary is inclusive: a record exactly 60 minutes old is still
 * live; one millisecond older is not.
 *
 * @returns {string|null}
 */
export const readCfDestination = () => {
  const { status, path } = inspectCfDestination();
  return status === 'live' ? path : null;
};

/** Write `path` with the current time as its stamp. Blocked storage is a no-op. */
const writeCfDestination = path => {
  try {
    window.localStorage.setItem(CF_DESTINATION_KEY, JSON.stringify({ path, storedAt: Date.now() }));
  } catch (e) {
    /* storage blocked — the destination still rides this navigation's router state */
  }
};

/**
 * Remember `from` as the destination whenever it is well-formed. The LAST
 * well-formed value wins and every capture restarts the 60-minute clock.
 *
 * 🔴 WHY LAST-WRITE-WINS HERE WHEN THE ORDER ID IS FIRST-WRITE-WINS. This value
 * feeds exactly one consumer: the Story 3.19 authenticated arrival at
 * `/l/new?cf_order=…`, which fires the attach for the id in THAT URL. Story
 * 3.19's own header (`readCfOrderFromSearch`, above) records first-write-wins
 * as PROVABLY WRONG for that consumer — it hands back the OLDER order on a
 * buyer's second arrival, which is by then spent, and the newer one never
 * attaches. A buyer who pays twice and arrives signed out twice must be sent
 * back with the order they paid for LAST. The order id's own first-write-wins
 * (`captureCfOrder`) is right for ITS readers and is untouched.
 *
 * A malformed candidate writes nothing and leaves a live value as it was.
 *
 * Safe to call from a render — on the server it is a no-op, and on the client
 * it changes no markup, so the SSR document and the hydrated tree agree.
 *
 * @param {string} from `${pathname}${search}${hash}` as `Routes.js` built it
 * @returns {string|null} the value now in storage, or null when there is none
 */
export const captureCfDestination = from => {
  if (!hasWindow()) {
    return null;
  }
  const candidate = normalizeCfDestination(from);
  if (!candidate) {
    return readCfDestination();
  }
  // A new claim supersedes whatever this page remembered of an older one.
  expiredHere = null;
  handedOff = null;
  writeCfDestination(candidate);
  return candidate;
};

export { CF_ORDER_KEY, CF_ORDER_ATTEMPTED_KEY, CF_DESTINATION_KEY, CF_DESTINATION_TTL_MS };
